import React, { useCallback, useEffect, useRef, useState } from "react";

import { ApiQuizAlumno, IntentoEnCurso, IntentoRevisado, PortadaQuiz } from "../../../../services/v2/lmsQuiz.service";
import { Aviso, Icono, LineaAviso, mensajeDeError, problema, useAlVolver } from "../comun";
import { esNotaBaja, puntosTxt } from "../tareas/comunTareas";
import { RespuestaVista } from "./respuestaVista";
import "../tareas/tareas.css";
import "./quiz.css";

/*
	El quiz del alumno (Portal de Clase, Parte D), celular primero: portada (preguntas, puntos, tiempo, intentos que le quedan) →
	Empezar → una pregunta por pantalla con barra de avance y reloj → Revisar → Entregar (con confirmación) → resultado según el
	ajuste del maestro. El reloj lo cuenta el SERVIDOR (deadlineMs y serverNowMs); aquí solo se dibuja cada segundo. Cada respuesta
	se guarda al cambiarla (el texto, al dejar de escribir); nada de sondeos. Si cierra la pestaña, al volver sigue donde iba con
	el tiempo que le queda. El alumno nunca recibe las correctas antes de lo que diga el ajuste: las manda el servidor o no llegan.
*/

type Valor = string | string[]
type Fase = "portada" | "contestando" | "revisar" | "resultado" | "respuestas"

const relojTxt = (seg: number): string => {
	const s = Math.max(0, Math.floor(seg))
	const h = Math.floor(s / 3600)
	const m = Math.floor((s % 3600) / 60)
	const r = String(s % 60).padStart(2, "0")
	return h > 0 ? h + ":" + String(m).padStart(2, "0") + ":" + r : m + ":" + r
}

const contestada = (v: Valor | undefined): boolean => (Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim() !== "")

/** Recuerda en qué pregunta iba (solo comodidad de este navegador; si falla, empieza en la primera sin contestar). */
const recordarIndice = (postId: number, n: number, i: number | null): void => {
	try { if (i === null) localStorage.removeItem("wapp.quiz." + postId); else localStorage.setItem("wapp.quiz." + postId, n + ":" + i) } catch { /* sin almacenamiento */ }
}
const leerIndice = (postId: number, n: number): number | null => {
	try { const v = localStorage.getItem("wapp.quiz." + postId); if (!v) return null; const [a, b] = v.split(":").map(Number); return a === n && !isNaN(b) ? b : null } catch { return null }
}

type EstadoGuardado = "guardando" | "ok" | "error"
interface ColaPregunta { deseado: Valor | null | undefined; enviado: Valor | null | undefined; error: boolean; timer: number | null; cadena: Promise<boolean> }

const mismoValor = (a: Valor | null | undefined, b: Valor | null | undefined): boolean => {
	const x = a === undefined || (Array.isArray(a) ? a.length === 0 : a === null || a === "") ? null : a
	const y = b === undefined || (Array.isArray(b) ? b.length === 0 : b === null || b === "") ? null : b
	if (x === null || y === null) return x === y
	if (Array.isArray(x) && Array.isArray(y)) return x.length === y.length && [...x].sort().join("\u0001") === [...y].sort().join("\u0001")
	return x === y
}

interface Props { apiQ: ApiQuizAlumno; postId: number; onVolver: () => void; onCambio: () => void }

const QuizAlumno: React.FC<Props> = ({ apiQ, postId, onVolver, onCambio }) => {
	const [portada, setPortada] = useState<PortadaQuiz | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [fase, setFase] = useState<Fase>("portada")
	const [intento, setIntento] = useState<IntentoEnCurso | null>(null)
	const [resp, setResp] = useState<Record<string, Valor>>({})
	const [idx, setIdx] = useState<number>(0)
	const [desfase, setDesfase] = useState<number>(0)
	const [ahora, setAhora] = useState<number>(Date.now())
	const [guardado, setGuardado] = useState<Record<string, EstadoGuardado>>({})
	const marcarGuardado = (k: string, e: EstadoGuardado) => setGuardado((g) => ({ ...g, [k]: e }))
	const [confirmar, setConfirmar] = useState<boolean>(false)
	const [revisado, setRevisado] = useState<IntentoRevisado | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(null)
	const [ocupado, setOcupado] = useState<boolean>(false)
	const [cargandoSeguir, setCargandoSeguir] = useState<boolean>(false)
	/*
		Guardado por pregunta: una sola cola por pregunta (nunca dos envíos a la vez, siempre el valor MÁS NUEVO al llegar su turno),
		el texto espera 900 ms a que deje de escribir, y lo que falló se reintenta en vaciar(). vaciar() espera TODO lo que esté en
		camino, así que Entregar nunca adelanta a la última respuesta.
	*/
	const cola = useRef<Record<string, ColaPregunta>>({})
	const entregando = useRef<boolean>(false)
	const intentoActivo = useRef<number | null>(null)

	const colaDe = (k: string): ColaPregunta => (cola.current[k] = cola.current[k] || { deseado: undefined, enviado: undefined, error: false, timer: null, cadena: Promise.resolve(true) })

	const entrar = (a: IntentoEnCurso) => {
		cola.current = {}
		Object.keys(a.answers || {}).forEach((k) => { const c = colaDe(k); c.deseado = a.answers[k]; c.enviado = a.answers[k] })
		intentoActivo.current = a.number
		setGuardado({})
		setConfirmar(false)
		setIntento(a)
		setResp(a.answers || {})
		setDesfase(a.serverNowMs - Date.now())
		const guardadoIdx = leerIndice(postId, a.number)
		const primera = a.questions.findIndex((q) => !contestada((a.answers || {})[String(q.id)]))
		setIdx(Math.min(a.questions.length - 1, Math.max(0, guardadoIdx ?? (primera >= 0 ? primera : 0))))
		setFase("contestando")
	}

	const cargar = useCallback(async (seguir: boolean): Promise<PortadaQuiz | null> => {
		try {
			const p = await apiQ.cover(postId)
			setPortada(p); setError(null); setDesfase(p.serverNowMs - Date.now())
			if (seguir && p.current) entrar(p.current)
			return p
		} catch (e) { setError(mensajeDeError(e, "No pudimos abrir el quiz.")); return null }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiQ, postId])
	useEffect(() => { cargar(true) }, [cargar])

	/** El intento se cerró en el servidor (se acabó el tiempo o el maestro cerró el quiz): muestra el resultado con el motivo real. */
	const cerradoEnServidor = async (p0?: PortadaQuiz) => {
		if (entregando.current) return
		entregando.current = true
		try {
			const p = p0 || await apiQ.submit(postId).catch(() => apiQ.cover(postId))
			const ultimo = [...p.attempts].sort((a, b) => b.number - a.number)[0]
			setPortada(p); setIntento(null); intentoActivo.current = null; setConfirmar(false); setFase("resultado"); recordarIndice(postId, 0, null)
			setAviso(problema(p.closed && !(ultimo && ultimo.closedByTime) ? "Tu maestro cerró el quiz: se calificó con lo que tenías guardado." : "Se acabó el tiempo: entregamos lo que tenías guardado."))
			onCambio()
		} catch (e) { setError(mensajeDeError(e)) } finally { entregando.current = false }
	}

	// Al volver a la pestaña: la portada se recarga; con el quiz abierto se vuelve a leer el tiempo del servidor (y si ya se cerró, se
	// muestra el resultado). Nunca se pide "Empezar" por su cuenta: eso podría abrir otro intento.
	useAlVolver(useCallback(() => {
		if (fase === "contestando" || fase === "revisar") {
			apiQ.cover(postId).then((p) => {
				setDesfase(p.serverNowMs - Date.now())
				if (p.current) { setPortada(p); setIntento((i) => (i ? { ...i, deadlineMs: p.current!.deadlineMs } : i)) }
				else cerradoEnServidor(p)
			}).catch(() => undefined)
		} else if (fase === "portada") cargar(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fase, apiQ, postId, cargar]), 5000)

	// Reloj en pantalla (solo dibuja; el límite lo hace cumplir el servidor).
	const conReloj = (fase === "contestando" || fase === "revisar") && !!intento?.deadlineMs
	useEffect(() => {
		if (!conReloj) return
		const t = window.setInterval(() => setAhora(Date.now()), 1000)
		return () => window.clearInterval(t)
	}, [conReloj])
	const restante = intento?.deadlineMs ? (intento.deadlineMs - (ahora + desfase)) / 1000 : null

	// ---- guardar respuestas
	/** Pone en la cola de la pregunta un envío; al llegar su turno manda el valor más nuevo (o nada si ya está guardado). */
	const enviar = (k: string): Promise<boolean> => {
		const c = colaDe(k)
		if (c.timer !== null) { window.clearTimeout(c.timer); c.timer = null }
		const numero = intentoActivo.current
		c.cadena = c.cadena.then(async () => {
			if (intentoActivo.current !== numero) return false
			const v = c.deseado === undefined ? null : c.deseado
			if (mismoValor(v, c.enviado)) { c.error = false; marcarGuardado(k, "ok"); return true }
			marcarGuardado(k, "guardando")
			try {
				const r = await apiQ.answer(postId, Number(k), contestada(v === null ? undefined : v) ? v : null)
				c.enviado = v; c.error = false
				setDesfase(r.serverNowMs - Date.now())
				// Si mientras viajaba cambió otra vez, el siguiente turno de la cola lo manda.
				marcarGuardado(k, mismoValor(c.deseado === undefined ? null : c.deseado, v) ? "ok" : "guardando")
				return true
			} catch (e: any) {
				c.error = true
				const code = e?.response?.data?.error_code
				if (code === "tiempo_agotado" || code === "sin_intento") { cerradoEnServidor(); return false }
				marcarGuardado(k, "error")
				return false
			}
		})
		return c.cadena
	}

	/** Manda lo que falta (texto a medio escribir, envíos fallidos) y espera lo que va en camino. true = todo guardado. */
	const vaciar = async (): Promise<boolean> => {
		const ks = Object.keys(cola.current)
		const r = await Promise.all(ks.map((k) => {
			const c = cola.current[k]
			return c.timer !== null || c.error || !mismoValor(c.deseado === undefined ? null : c.deseado, c.enviado) ? enviar(k) : c.cadena
		}))
		return r.every(Boolean) && ks.every((k) => !cola.current[k].error)
	}

	const responder = (qid: number, v: Valor, escribiendo = false) => {
		const k = String(qid)
		const c = colaDe(k)
		c.deseado = v
		setResp((r) => ({ ...r, [k]: v }))
		if (escribiendo) {
			if (c.timer !== null) window.clearTimeout(c.timer)
			marcarGuardado(k, "guardando")
			c.timer = window.setTimeout(() => { c.timer = null; enviar(k) }, 900)
		} else enviar(k)
	}

	// Al esconder la pestaña se manda lo pendiente; si la página se cierra, lo escrito sale con keepalive.
	useEffect(() => {
		if (fase !== "contestando" && fase !== "revisar") return
		const alEsconder = () => { if (document.visibilityState === "hidden") vaciar() }
		const alSalir = () => {
			Object.keys(cola.current).forEach((k) => {
				const c = cola.current[k]
				const v = c.deseado === undefined ? null : c.deseado
				if (c.timer !== null || c.error || !mismoValor(v, c.enviado)) apiQ.answerAlSalir(postId, Number(k), contestada(v === null ? undefined : v) ? v : null)
			})
		}
		document.addEventListener("visibilitychange", alEsconder)
		window.addEventListener("pagehide", alSalir)
		return () => { document.removeEventListener("visibilitychange", alEsconder); window.removeEventListener("pagehide", alSalir) }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fase, apiQ, postId])

	const vencio = conReloj && restante !== null && restante <= 0
	useEffect(() => {
		if (vencio) vaciar().finally(() => cerradoEnServidor())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vencio])

	useEffect(() => { if (intento && fase === "contestando") recordarIndice(postId, intento.number, idx) }, [intento, idx, fase, postId])

	// ---- acciones
	/** Empezar o seguir: siempre con lo que tiene el servidor (nunca con una copia vieja de la portada). */
	const empezar = async () => {
		setOcupado(true); setCargandoSeguir(true); setAviso(null)
		try { const r = await apiQ.start(postId); entrar(r.attempt); onCambio() } catch (e) { setAviso(problema(mensajeDeError(e))); cargar(false) } finally { setOcupado(false); setCargandoSeguir(false) }
	}
	const ir = async (i: number) => { await vaciar(); setIdx(i); setFase("contestando"); window.scrollTo(0, 0) }
	const revisar = async () => { await vaciar(); setFase("revisar"); window.scrollTo(0, 0) }
	const entregar = async () => {
		setConfirmar(false)
		setOcupado(true); setAviso(null)
		try {
			if (!(await vaciar())) {
				if (intentoActivo.current !== null) setAviso(problema("Hay respuestas sin guardar (Sin Guardar). Revisa tu conexión y vuelve a tocar Entregar."))
				return
			}
			entregando.current = true
			const p = await apiQ.submit(postId)
			setPortada(p); setIntento(null); intentoActivo.current = null; setFase("resultado"); recordarIndice(postId, 0, null); onCambio(); window.scrollTo(0, 0)
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false); entregando.current = false }
	}
	const verRespuestas = async (n: number) => {
		setOcupado(true); setAviso(null)
		try { setRevisado(await apiQ.attempt(postId, n)); setFase("respuestas"); window.scrollTo(0, 0) } catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}
	const salir = async () => {
		const ok = await vaciar()
		if (!ok && intentoActivo.current !== null && !window.confirm("Hay respuestas que no se guardaron. ¿Salir de todos modos? Al volver se reintenta lo que siga en esta pantalla.")) return
		setFase("portada"); cargar(false)
	}

	if (error && !portada) return <div className="pc"><div className="pcPagina pcPaginaAlumno"><button type="button" className="pcEnlaceBtn" onClick={onVolver}>Mis Tareas</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (!portada) return <div className="pc"><div className="pcCargando">Cargando…</div></div>
	const p = portada

	// ---- contestando / revisar
	if ((fase === "contestando" || fase === "revisar") && intento) {
		const qs = intento.questions
		const total = qs.length
		const hechas = qs.filter((q) => contestada(resp[String(q.id)])).length
		const sinGuardar = qs.filter((q) => guardado[String(q.id)] === "error").length
		const reintentar = async () => { setOcupado(true); setAviso(null); const ok = await vaciar(); setOcupado(false); if (!ok && intentoActivo.current !== null) setAviso(problema("Todavía no se pudo guardar. Revisa tu conexión y vuelve a intentar.")) }
		const poco = restante !== null && restante <= 60
		const arriba = (
			<div className="qzArriba">
				<div className="qzArribaFila">
					<button type="button" className="pcIb" style={{ marginLeft: -10 }} aria-label="Salir del quiz (tus respuestas quedan guardadas)" onClick={salir}><Icono n="cerrar" size={22} /></button>
					<span>{p.title}</span>
					{restante !== null ? <span className={"qzReloj" + (poco ? " qzRelojOro" : "")} role="timer" aria-label={"Te quedan " + relojTxt(restante)}><Icono n="reloj" size={16} />{relojTxt(restante)}</span> : null}
				</div>
				<div className="qzAvance" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={fase === "revisar" ? hechas : idx + 1} aria-label="Avance"><div style={{ width: ((fase === "revisar" ? hechas : idx + 1) / Math.max(1, total)) * 100 + "%" }} /></div>
			</div>
		)
		if (fase === "revisar") {
			return (
				<div className="pc">
					<div className="pcPagina pcPaginaAlumno" style={{ paddingBottom: 24 }}>
						{arriba}
						<h1 className="pcT" style={{ margin: "6px 0 0", fontSize: 24, fontWeight: 800 }}>Revisa Tus Respuestas</h1>
						<div className={hechas < total ? "tkAvisoOro" : "pcNota"}>{hechas === total ? "Contestaste todas las preguntas." : "Contestaste " + hechas + " de " + total + ". Las que faltan cuentan 0."}</div>
						{sinGuardar > 0 ? <div className="tkAvisoOro" role="alert">{sinGuardar === 1 ? "1 respuesta no se ha guardado" : sinGuardar + " respuestas no se han guardado"} (Sin Guardar). Revisa tu conexión y toca Reintentar Guardar; no puedes entregar hasta que se guarden.</div> : null}
						<div className="qzCuadros" role="list" aria-label="Preguntas">
							{qs.map((q, i) => {
								const g = guardado[String(q.id)]
								const sinGuardar = g === "error" || g === "guardando"
								const ok = contestada(resp[String(q.id)]) && !sinGuardar
								const txt = g === "error" ? "Sin Guardar" : g === "guardando" ? "Guardando" : ok ? "Lista" : "Falta"
								return <button key={q.id} type="button" role="listitem" className={"qzCuadro " + (ok ? "qzCuadroOk" : "qzCuadroFalta")} aria-label={"Pregunta " + (i + 1) + ", " + txt.toLowerCase()} onClick={() => ir(i)}>{i + 1}<small>{txt}</small></button>
							})}
						</div>
						<LineaAviso a={aviso} />
						<div className="qzPieQuiz">
							<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => ir(Math.min(idx, total - 1))}>Volver</button>
							{sinGuardar > 0 ? <button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={reintentar}>Reintentar Guardar</button>
								: <button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => setConfirmar(true)}>Entregar</button>}
						</div>
					</div>
					{confirmar ? (
						<div className="qzHoja" role="dialog" aria-modal="true" aria-labelledby="qzConfirmaTitulo">
							<div className="qzHojaCaja">
								<h2 id="qzConfirmaTitulo">¿Entregar El Quiz?</h2>
								<div style={{ fontSize: 15, lineHeight: 1.45 }}>{hechas < total ? "Te faltan " + (total - hechas) + (total - hechas === 1 ? " pregunta" : " preguntas") + " por contestar. " : ""}Después de entregar ya no puedes cambiar tus respuestas.</div>
								<div className="qzHojaBotones">
									<button type="button" className="pcBtn pcBtnS" onClick={() => setConfirmar(false)}>Seguir Contestando</button>
									<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={entregar}>Entregar</button>
								</div>
							</div>
						</div>
					) : null}
				</div>
			)
		}
		const q = qs[Math.min(idx, total - 1)]
		if (!q) return <div className="pc"><div className="pcCargando">Cargando…</div></div>
		const v = resp[String(q.id)]
		const estadoGuardado = guardado[String(q.id)] || ""
		const elegidas: string[] = Array.isArray(v) ? v : typeof v === "string" ? [v] : []
		const elegir = (oid: string) => {
			if (q.type === "casillas") responder(q.id, elegidas.includes(oid) ? elegidas.filter((x) => x !== oid) : [...elegidas, oid])
			else responder(q.id, oid)
		}
		const ultima = idx >= total - 1
		return (
			<div className="pc">
				<div className="pcPagina pcPaginaAlumno" style={{ paddingBottom: 24 }}>
					{arriba}
					<div className="pcTenue pcChico" style={{ fontWeight: 700, marginTop: 4 }}>Pregunta {idx + 1} De {total} · {puntosTxt(q.points)} {q.points === 1 ? "Punto" : "Puntos"}{q.type === "casillas" ? " · Elige Todas Las Correctas" : ""}</div>
					<h1 className="qzEnunciado" id={"qzEn" + q.id}>{q.text}</h1>
					{q.imageUrl ? <div className="qzFoto"><img src={q.imageUrl} alt={"Imagen de la pregunta " + (idx + 1)} /></div> : null}
					{q.type === "opcion" || q.type === "vf" || q.type === "casillas" ? (
						<div className="qzElegir" role={q.type === "casillas" ? "group" : "radiogroup"} aria-labelledby={"qzEn" + q.id}>
							{q.options.map((o) => {
								const on = elegidas.includes(o.id)
								return (
									<button key={o.id} type="button" role={q.type === "casillas" ? "checkbox" : "radio"} aria-checked={on} className="qzElegirOpcion" onClick={() => elegir(o.id)}>
										<span className={"qzBolita" + (q.type === "casillas" ? " qzBolitaCuadro" : "")} aria-hidden="true">{on ? <Icono n="check" size={14} /> : null}</span>
										<span>{o.text}</span>
									</button>
								)
							})}
						</div>
					) : q.type === "corta" ? (
						<input className="qzEscribir" aria-labelledby={"qzEn" + q.id} maxLength={300} placeholder="Tu Respuesta" value={typeof v === "string" ? v : ""} autoComplete="off"
							onChange={(e) => responder(q.id, e.target.value, true)} onBlur={() => vaciar()} />
					) : (
						<textarea className="qzEscribir" aria-labelledby={"qzEn" + q.id} rows={7} maxLength={5000} placeholder="Escribe Tu Respuesta" value={typeof v === "string" ? v : ""}
							onChange={(e) => responder(q.id, e.target.value, true)} onBlur={() => vaciar()} />
					)}
					<div className="qzGuardado" role="status" style={estadoGuardado === "error" ? { color: "var(--pc-oro-texto)", fontWeight: 700 } : undefined}>
						{estadoGuardado === "guardando" ? "Guardando…" : estadoGuardado === "ok" ? "Guardado" : estadoGuardado === "error" ? "Sin Guardar: revisa tu conexión. Lo reintentamos al cambiar de pregunta y antes de entregar." : ""}
					</div>
					<div className="qzPieQuiz">
						<button type="button" className="pcBtn pcBtnS" disabled={idx === 0} onClick={() => ir(idx - 1)}>Anterior</button>
						{ultima ? <button type="button" className="pcBtn pcBtnP" onClick={revisar}>Revisar</button> : <button type="button" className="pcBtn pcBtnP" onClick={() => ir(idx + 1)}>Siguiente</button>}
					</div>
				</div>
			</div>
		)
	}

	// ---- respuestas de un intento entregado
	if (fase === "respuestas" && revisado) {
		return (
			<div className="pc">
				<div className="pcPagina pcPaginaAlumno">
					<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<button type="button" className="pcIb" style={{ marginLeft: -10 }} aria-label="Volver al quiz" onClick={() => { setFase("portada"); setRevisado(null) }}><Icono n="atras" size={22} /></button>
						<span style={{ flex: 1, fontWeight: 700, fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
					</div>
					<h1 className="pcT" style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Intento {revisado.number}</h1>
					<div className="pcTenue pcChico">{["Entregado " + (revisado.submittedLabel || ""), !revisado.scoreHidden && revisado.score !== null ? puntosTxt(revisado.score) + " / " + puntosTxt(p.points) + " Pts" : null].filter(Boolean).join(" · ")}</div>
					{!revisado.answersVisible ? <div className="pcNota">Aquí ves lo que contestaste. {p.answersNote || "Las respuestas correctas aún no se muestran."}</div> : null}
					<div className="pcTarjeta">{revisado.questions.map((q, i) => <RespuestaVista key={q.id} q={q} n={i + 1} />)}</div>
				</div>
			</div>
		)
	}

	// ---- resultado (recién entregado) y portada
	const recien = fase === "resultado" ? p.attempts.find((a) => a.number === p.submitted) || [...p.attempts].sort((a, b) => b.number - a.number)[0] || null : null
	const tiempoTxt = p.settings.timeLimitMin ? p.settings.timeLimitMin + " Min" : "Sin Límite"
	const puedeSeguir = !!p.current
	const vencido = p.overdue && !p.closed
	return (
		<div className="pc">
			<div className="pcPagina pcPaginaAlumno">
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<button type="button" className="pcIb" style={{ marginLeft: -10 }} aria-label="Volver a las tareas" onClick={onVolver}><Icono n="atras" size={22} /></button>
					<span style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>Mis Tareas</span>
				</div>

				{recien ? (
					<section className="pcTarjeta qzResultado" aria-label="Resultado">
						<div className="pcEtiqueta">Quiz Entregado</div>
						{!recien.scoreHidden && !recien.pending && recien.final !== null ? (
							<div><span className={"tkNumeroGrande" + (esNotaBaja(recien.final, p.points) ? " tkNumeroOro" : "")}>{puntosTxt(recien.final)}</span> <span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(p.points)}</span></div>
						) : null}
						<div style={{ fontSize: 15, lineHeight: 1.45 }}>
							{recien.pending ? "Tu maestro califica las preguntas abiertas. Verás tu nota cuando la devuelva."
								: recien.scoreHidden || recien.final === null ? "Tu nota se ve cuando tu maestro la devuelva."
									: recien.lateDiscount > 0 ? "Incluye un descuento de " + puntosTxt(recien.lateDiscount) + " pts por entregar tarde." : aviso ? "" : "¡Listo! Ya quedó guardado."}
						</div>
						{recien.closedByTime ? <div className="tkAvisoOro">Se acabó el tiempo: se calificó con lo que tenías guardado.</div> : null}
						{!recien.answersVisible && p.answersNote ? <div className="pcTenue pcChico">{p.answersNote}</div> : null}
						<LineaAviso a={aviso} />
						<div className="qzHojaBotones" style={{ marginTop: 6 }}>
							<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => verRespuestas(recien.number)}>{recien.answersVisible ? "Ver Respuestas Correctas" : "Ver Mis Respuestas"}</button>
							<button type="button" className="pcBtn pcBtnP" onClick={() => { setFase("portada"); setAviso(null) }}>Listo</button>
						</div>
					</section>
				) : (
					<>
						<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
							<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
								{p.unit ? <span className="pcChip">{p.unit}</span> : null}
								<span className="pcChip pcChipBorde">Quiz</span>
								{p.closed ? <span className="pcChip pcChipOro">Cerrado</span> : null}
							</div>
							<h1 className="pcT" style={{ margin: 0, fontSize: 26, fontWeight: 800, lineHeight: 1.1, overflowWrap: "anywhere" }}>{p.title}</h1>
							{p.dueLabel ? <div style={{ fontSize: 14, fontWeight: 700, color: vencido && p.attempts.length === 0 ? "var(--pc-oro-texto)" : "var(--pc-acento-texto)" }}>Vence {p.dueLabel}</div> : null}
						</div>
						{p.body ? <div className="pcTarjeta" style={{ fontSize: 14.5, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{p.body}</div> : null}
						<div className="qzPortadaDatos">
							<div><small>Preguntas</small><b>{p.questions}</b></div>
							<div><small>Puntos</small><b>{puntosTxt(p.points)}</b></div>
							<div><small>Tiempo</small><b>{tiempoTxt}</b></div>
							<div><small>{p.attemptsLeft === 1 ? "Te Queda" : "Te Quedan"}</small><b>{p.attemptsLeft} De {p.settings.maxAttempts}</b>{p.settings.maxAttempts > 1 ? <small style={{ fontWeight: 600 }}>Cuenta El Mejor</small> : null}</div>
						</div>
						{p.blocked && !puedeSeguir ? <div className="tkAvisoOro" role="status">{p.blocked.message}</div> : null}
						{vencido && p.canStart && p.acceptLate ? <div className="tkAvisoOro" role="status">Ya venció{p.latePenaltyPct > 0 ? ": si lo haces ahora se te resta " + puntosTxt(p.latePenaltyPct) + "% por cada día tarde." : ", pero todavía lo puedes hacer."}</div> : null}
						{p.settings.timeLimitMin && p.canStart && !puedeSeguir ? <div className="pcNota">Tienes {p.settings.timeLimitMin === 1 ? "1 minuto" : p.settings.timeLimitMin + " minutos"} desde que tocas Empezar. Si sales, el reloj sigue corriendo.</div> : null}

						{p.grade ? (
							<div className="pcTarjeta tkMiNota" aria-label="Tu nota">
								<div className="pcEtiqueta">Tu Nota</div>
								{p.grade.points !== null && !p.grade.pointsHidden ? (
									<div><span className={"tkNumeroGrande" + (esNotaBaja(p.grade.points, p.points) ? " tkNumeroOro" : "")}>{puntosTxt(p.grade.points)}</span> <span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(p.points)}</span></div>
								) : <div className="pcTenue">Tu maestro ya la devolvió. Tu nota se publica más adelante.</div>}
								{p.grade.lateDiscount > 0 ? <div className="pcTenue pcChico">Incluye un descuento de {puntosTxt(p.grade.lateDiscount)} pts por entrega tarde.</div> : null}
								{p.grade.comment ? <div className="tkComentarioAlumno" style={{ textAlign: "left" }}><b>Tu Maestro:</b> {p.grade.comment}</div> : null}
							</div>
						) : null}

						{p.attempts.length > 0 ? (
							<>
								<div className="pcSeccion">Tus Intentos</div>
								<div className="pcTarjeta" style={{ padding: 0, gap: 0, overflow: "hidden" }}>
									{p.attempts.map((a) => (
										<div className="qzIntentoFila" key={a.number}>
											<span>
												<b>Intento {a.number}</b>{a.counts && p.attempts.length > 1 ? <span className="pcChip pcChipAcento" style={{ height: 22, fontSize: 11.5, marginLeft: 6 }}>Cuenta</span> : null}
												<span className="pcTenue pcChico" style={{ display: "block" }}>{[a.submittedLabel, a.late ? "Tarde" : null, a.closedByTime ? "Se acabó el tiempo" : null].filter(Boolean).join(" · ")}</span>
											</span>
											<span style={{ fontWeight: 800 }}>{a.pending ? <span className="pcChip">En Revisión</span> : a.scoreHidden || a.final === null ? <span className="pcChip">Entregado</span> : <span className={esNotaBaja(a.final, p.points) ? "qzOro" : undefined}>{puntosTxt(a.final)} / {puntosTxt(p.points)}</span>}</span>
											<button type="button" className="pcBtn pcBtnS pcBtnChico" disabled={ocupado} onClick={() => verRespuestas(a.number)}>Ver</button>
										</div>
									))}
								</div>
							</>
						) : null}
						{p.attempts.length > 0 && p.answersNote && !p.answersVisible ? <div className="pcNota">{p.answersNote}</div> : null}
						<LineaAviso a={aviso} />
						{puedeSeguir || p.canStart ? (
							<div className="tkPieEntrega">
								<button type="button" className="pcBtn pcBtnP" disabled={ocupado || cargandoSeguir} onClick={empezar}>
									{puedeSeguir ? "Seguir Con El Quiz" : p.attempts.length > 0 ? "Intentar De Nuevo" : "Empezar"}
								</button>
							</div>
						) : null}
					</>
				)}
			</div>
		</div>
	)
}

export default QuizAlumno
